var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { space, width, maxWidth, display, alignItems, flexWrap, } from 'styled-system';
import { boxSizing } from '../../common/theme/theme';
import AccordionSection from '../../common/components/AccordionSection';
import { getTrackerProps } from '../../common/tracking/getTrackerProps';
import { Link } from '../../common/components/Link';
import { Text } from '../../common/components/Text';
import { LazyImage } from '../../common/components/LazyImage';
import { placeholderThumbnail } from '../../common/utils/placeholderThumbnail';
var FIRST_SECTION_NAME = 'general';
var LinkSectionList = styled.ul(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  ", ";\n  ", ";\n  ", ";\n  list-style: none;\n"], ["\n  ", ";\n  ", ";\n  ", ";\n  list-style: none;\n"])), display, space, flexWrap);
LinkSectionList.defaultProps = {
    px: 0,
    pt: 'sp_16',
    pb: ['sp_24', null, 'sp_16'],
    m: 0,
    flexWrap: 'wrap',
};
var LinkSectionListItem = styled.li(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  ", ";\n  ", ";\n  ", ";\n  ", ";\n  ", ";\n  max-width: 100%;\n"], ["\n  ", ";\n  ", ";\n  ", ";\n  ", ";\n  ", ";\n  max-width: 100%;\n"])), display, width, space, alignItems, boxSizing);
LinkSectionListItem.defaultProps = {
    display: 'flex',
    alignItems: 'flex-start',
    boxSizing: 'border-box',
};
var LinkSectionItem = styled(Text)(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  ", ";\n"], ["\n  ", ";\n"])), width);
LinkSectionItem.defaultProps = __assign(__assign({}, Text.defaultProps), { width: 1, fontSize: ['fs_14', null, null, 'fs_16'], lineHeight: ['lh_20', null, null, 'lh_24'] });
var LinkSectionItemLink = styled(Link)(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  display: block;\n  width: 100%;\n\n  /* Custom link styles for the footer */\n  text-decoration: none !important;\n\n  &:active {\n    font-weight: bold;\n  }\n"], ["\n  display: block;\n  width: 100%;\n\n  /* Custom link styles for the footer */\n  text-decoration: none !important;\n\n  &:active {\n    font-weight: bold;\n  }\n"])));
LinkSectionItemLink.defaultProps = __assign(__assign({}, Link.defaultProps), { fontSize: ['fs_14', null, null, 'fs_16'], lineHeight: ['lh_20', null, null, 'lh_24'] });
var LinkSectionImage = styled.img(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n  ", ";\n  ", ";\n"], ["\n  ", ";\n  ", ";\n"])), display, maxWidth);
LinkSectionImage.defaultProps = {
    display: 'block',
    maxWidth: '100%',
};
var LinkSectionGroup = function (_a) {
    var title = _a.title, items = _a.items, isExpanded = _a.isExpanded, isCollapsible = _a.isCollapsible, onTitleClick = _a.onTitleClick, sectionName = _a.sectionName;
    var open = isExpanded(sectionName);
    var isBorderTopNeeded = sectionName === FIRST_SECTION_NAME;
    var isAnyImage = items.some(function (item) { return !!item.image; });
    return (React.createElement(AccordionSection, { displayBorder: ['block', null, 'none'], hasTopBorder: isBorderTopNeeded, title: title, sectionName: sectionName, onTitleClick: onTitleClick, isCollapsible: isCollapsible, isExpanded: isExpanded },
        React.createElement(LinkSectionList, { display: [open ? 'flex' : 'none', null, 'flex'], 
            /** We are adjusting the column margin to match the items offsets */
            mr: isAnyImage ? '-16px' : ['-16px', '-24px', null, '-32px'], "data-testid": "link-section-list" }, items.map(function (item, index) {
            var Item = item.href ? LinkSectionItemLink : LinkSectionItem;
            return (React.createElement(LinkSectionListItem, { key: index, pr: item.image ? 'sp_16' : ['sp_16', 'sp_24', null, 'sp_32'], py: item.image ? 'sp_8' : 'sp_4', width: item.image ? 'auto' : [1 / 2, 1 / 4, 1 / 2, 1 / 3], "data-testid": "link-section-list-item-" + index },
                React.createElement(Item, __assign({}, getTrackerProps({
                    'data-testid': 'link-section-link',
                    href: item.href,
                    title: item.title,
                    onClick: function (event) {
                        // Prevent tracking if the element is not an anchor or image
                        event.nativeEvent.trackingPreventDefault = ![
                            'A',
                        ].includes(event.currentTarget.nodeName);
                    },
                }, __assign(__assign({ event: 'footerTracking' }, (item.image && { eventCategory: 'image' })), { eventData: __assign({ placement: 'linkSection', labelIdentifier: 'h24_footer_link_section' }, (item.image && { label: item.name })) }))), item.image ? (React.createElement(LazyImage, { x1: item.image.src }, function (_a) {
                    var loaded = _a.loaded, image = _a.image, thumbnail = _a.thumbnail, innerRef = _a.innerRef;
                    return (React.createElement(LinkSectionImage, { "data-testid": "link-section-image", alt: item.name, ref: innerRef, src: loaded
                            ? image
                            : placeholderThumbnail(item.image.width, item.image.height), style: {
                            width: item.image.width,
                            height: item.image.height,
                        } }));
                })) : (React.createElement("span", { "data-testid": "link-section-link-label" }, item.name)))));
        }))));
};
export var LinkSectionDataShape = {
    /** Link section title */
    title: PropTypes.string,
    /** Section items */
    items: PropTypes.arrayOf(PropTypes.shape({
        /** Item label */
        name: PropTypes.string,
        /** Item href */
        href: PropTypes.string,
        /** Link title */
        title: PropTypes.string,
        /** Item image */
        image: PropTypes.shape({
            /** Image width */
            width: PropTypes.number,
            /** Image height */
            height: PropTypes.number,
            /** Image source */
            src: PropTypes.string,
        }),
    })),
};
LinkSectionGroup.propTypes = __assign(__assign({}, LinkSectionDataShape), { 
    /** Method that checks if section should be open based on section name */
    isExpanded: PropTypes.func, 
    /** Is the accordion behaviour active */
    isActive: PropTypes.bool, 
    /** Event callback for title click */
    onTitleClick: PropTypes.func, 
    /** Link section name */
    sectionName: PropTypes.string });
LinkSectionGroup.defaultProps = {
    isCollapsible: false,
};
export default LinkSectionGroup;
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5;
